<template>
  <div class="map" :class="{ tool: !!tool }">

    <l-map ref="map"
           v-if="elements"
           :zoom="map.config.zoom"
           :center="map.config.center"
           :options="map.options"
           @click="click($event)"
           @contextmenu="contextmenu($event)"
           @editable:drawing:commit="created($event)">

      <!-- Open Street Maps -->
      <l-tile-layer v-if="map.config.style === 'osm'"
                    v-for="(item, index) in map.providers"
                    :key="index"
                    :url="item.url"
                    :attribution="item.attribution"
                    :name="item.name"
                    :visible="item.visible"
                    layer-type="base" />

      <l-control-layers position="topright" v-if="map.config.style === 'osm'" />

      <!-- Google Maps -->
      <google-maps-layer v-if="map.config.style === 'google'"
                         :styles="['roadmap', 'satellite', 'terrain', 'hybrid' ]" />


      <map-controls :tool="tool"
                    :filter="filter"
                    @filter="filter = $event"
                    @tool="selectTool($event)"
                    @locked="locked($event)"
                    @zoom="zoom($event)" />

      <map-elements-list :elements="visibleElements"
                         @click="click($event.element)"
                         @menu="contextmenu($event.event, $event.data)" />

    </l-map>

    <loader v-if="!elements"></loader>

    <vue-context ref="menu">
      <template slot-scope="child" v-if="child.data">
        <li v-for="(item, index) in child.data" :key="index">
          <a @click.prevent="item.action()">
            <font-awesome-icon :fixed-width="true" :icon="item.icon" v-if="item.icon"></font-awesome-icon>
            {{ item.title }}
          </a>
        </li>
      </template>
    </vue-context>

  </div>
</template>

<script>

import { mapState, mapActions } from 'vuex'
import { LMap, LTileLayer, LControlLayers } from 'vue2-leaflet'
import { VueContext } from 'vue-context'
import 'vue-context/src/sass/vue-context.scss'
import Loader from '../../shared/loader'
import GoogleMapsLayer from './google-maps-layer'
import MapControls from './map-controls'
import MapElementsList from './map-elements-list'
import i18n from "@/i18n";

export default {
  name: 'surface',

  props: [ 'assetId' ],

  data () {
    return {
      filter: {},
      tool: null,
      toolOptions: null,
      tools: {
        marker: { select: () => this.editor.startMarker(undefined, { foo: 'bar' }), locations: element => element._latlng },
        polygon: { select: () => this.editor.startPolygon(), locations: element => element._latlngs[0] },
        polyline: { select: () => this.editor.startPolyline(), locations: element => element._latlngs },
        rectangle: { select: () => this.editor.startRectangle(), locations: element => ({ sw: element._bounds._southWest, ne: element._bounds._northEast }) },
        circle: { select: () => this.editor.startCircle(), locations: element => ({ center: element._latlng, radius: element._mRadius }) },
      },
      map: {
        config: this.$store.state.gmap,
        options: {
          zoomControl: false,
          zoomSnap: 0.5,
          editable: true
        },
        providers: [
          {
            name: this.$t('map.controls.osm.roadmap'),
            visible: true,
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
          },
          {
            name: this.$t('map.controls.osm.satellite'),
            visible: false,
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
          }
        ]
      }
    }
  },

  computed: {
    ...mapState('map', [
      'elements',
      'selected'
    ]),

    editor () {
      return this.$refs.map.mapObject.editTools
    },

    visibleElements () {
      return this.elements.filter(element => !this.filter.assetTypes
        || (element.asset && this.filter.assetTypes.indexOf(element.asset.assetTypeId) >= 0)
        || element.type !== 'marker')
    }
  },

  methods: {
    ...mapActions('map', [
      'select',
      'save'
    ]),

    click (element) {
      if (!this.tool)
        this.select(element)
    },

    contextmenu (event, data) {
      if (data)
        this.$refs.menu.open(event.originalEvent, data)
    },

    selectTool (tool, options = {}) {
      if (this.tool)
        this.editor.commitDrawing()

      this.select(null)
      this.tool = tool
      this.editor.stopDrawing()

      const controller = this.tools[this.tool]

      if (controller) {
        this.toolOptions = options
        controller.select()
      }
    },

    created (event) {
      const locations = this.tools[this.tool].locations(event.layer)

      this.save(Object.assign({
        type: this.tool,
        locations: this.flatten(locations)
      }, this.toolOptions || {}))

      // reset tool and remove from map
      event.layer.remove()
      this.tool = null
    },

    locked (locked) {
      if (locked && this.tool)
        this.editor.commitDrawing()
    },

    flatten (location) {
      if (location.lat !== undefined && location.lng !== undefined)
        return { lat: location.lat, lng: location.lng }

      if (Array.isArray(location))
        return location.map(x => this.flatten(x))

      if (typeof location === 'object') {
        Object.keys(location).forEach(property => location[property] = this.flatten(location[property]))
        return location
      }

      return location
    },

    zoom (direction) {
      const map = this.$refs.map.mapObject
      direction > 0 ? map.zoomIn() : map.zoomOut()
    },

    async createNonExistent () {
      const asset = await this.$store.dispatch('getItem', { modelName: 'assets', id: this.assetId })
      const result = await this.$dialog.confirm({
        title: this.$t('map.element.element-not-found'),
        text: this.$t('map.element.create-automatic', [ asset.name ]),
        actions: {
          true: {text: i18n.t('yes'), color: 'red'},
          false: {text: i18n.t('no'), color: 'black'}
        }
      })

      if (result)
        this.selectTool('marker', { assetId: this.assetId })
    }

  },

  watch: {
    selected (value) {
      if (!value || !value.locations)
        return

      const location = [].concat(value.locations)[0]
      const map = this.$refs.map.mapObject

      if (!map.getBounds().contains(location))
        map.flyTo(location)
    }
  },

  async mounted () {
    if (!this.assetId)
      return

    const elements = this.assetId ? this.elements.filter(x => x.assetId === this.assetId) : []

    if (elements.length)
      this.$nextTick(() => this.select(elements[0]))
    else
      this.createNonExistent()
  },

  components: {
    Loader,
    MapElementsList,
    LMap,
    LControlLayers,
    LTileLayer,
    VueContext,
    GoogleMapsLayer,
    MapControls,
  }
}
</script>

<style lang="scss" scoped>

  @import '../../../assets/variables';
  @import '../../../assets/responsive';

  .map {
    flex: 1 1 100%;
    position: relative;

    .vue-map-container {
      height: 100%;
    }

    &.tool {
      .leaflet-grab {
        cursor: crosshair !important;
      }
    }

    .vue2leaflet-map {
      z-index: 0;
    }

    .v-context {
      border-radius: $map-control-radius;
      padding: 0;

      & > li > a {
        padding: 5px 10px;
        font-size: 13px;
        color: $color-primary;
      }
    }

    .loader {
      height: 100%;
    }
  }

  @include media('<tablet') {
      .map {
        height: 100%;
        width: 100%;
      }
  }
</style>

